import { isValidElement, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Callout, CopyButton } from '@/shared/ui';
import type { DraftSection } from '../types';
import { SETTINGS_CHANGE_KEYS, settingsChangeText } from '../utils/settingsChangeText';
import styles from './DraftSections.module.css';

// A draft laid out by its skill's output template. Sections render by ROLE, never by skill, so a
// GBP post, a blog post and an ad creative all read the same way:
//   context  a compact line of what the draft is about
//   content  the thing that gets published or applied
//   check    what a person must confirm before approving
//   why      the rationale, last and quiet

// A closed draft (skipped, rejected or dropped) was never approved, so its "approve" wording is
// rewritten to say so. Only the sentences the ad change skill writes need it: the one drafts carry
// now (applied on approval) and the one older drafts carry (made by hand).
const APPROVE_SENTENCE = /;?\s*if you approve it, make the change in the ad account yourself\./i;
const APPLIED_ON_APPROVAL = /Applied in Google Ads when you approve:[^\n]*?drafted again\./i;
function closedWording(text: string, closed: boolean): string {
  return closed
    ? text
        .replace(
          APPROVE_SENTENCE,
          '. It was not approved, so no change is needed in the ad account.',
        )
        .replace(APPLIED_ON_APPROVAL, 'It was not approved, so nothing was changed in Google Ads.')
    : text;
}

const CLOSED_CHECKS_TITLE = 'Checks the draft listed (not approved)';

function asText(value: DraftSection['value']): string {
  return Array.isArray(value) ? value.join('\n') : value;
}

function textOf(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

// A fenced code block is the draft's unit of "paste this exactly" (a meta title, a schema snippet),
// so each one gets its own copy button. A one-line value also shows its length, which is what the
// reviewer checks for a title or description.
function CodeBlock({ children }: { children?: ReactNode }) {
  const value = textOf(children).replace(/\n$/, '');
  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeBar}>
        {!value.includes('\n') && <span className={styles.count}>{value.length} characters</span>}
        <CopyButton value={value} />
      </div>
      <pre className={styles.pre}>{children}</pre>
    </div>
  );
}

const DRAFT_MARKDOWN: Components = {
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
};

function Markdown({ text }: { text: string }) {
  return (
    <div className={styles.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={DRAFT_MARKDOWN}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

// Checks are numbered so the practice can refer to one ("item 2 is wrong"); other lists are bulleted.
function ListValue({ items, ordered = false }: { items: string[]; ordered?: boolean }) {
  const List = ordered ? 'ol' : 'ul';
  return (
    <List className={ordered ? styles.orderedList : styles.list}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </List>
  );
}

// `showLabel` is false for a draft's only content field: its label ("Proposed change") just
// restates that this is the draft.
function ContentField({
  section,
  showLabel,
  closed,
}: {
  section: DraftSection;
  showLabel: boolean;
  closed: boolean;
}) {
  const settingLines = SETTINGS_CHANGE_KEYS.has(section.key);
  const raw = closedWording(asText(section.value), closed);
  // Setting changes are stored as `field: old -> new`; the practice reads them in words.
  const text = settingLines ? settingsChangeText(raw) : raw;
  const showCount = section.maxChars !== undefined && section.format !== 'list' && !settingLines;
  return (
    <div className={styles.field}>
      {(showLabel || showCount) && (
        <div className={styles.fieldHeader}>
          {showLabel && <span className={styles.label}>{section.label}</span>}
          {showCount && (
            <span className={styles.count}>
              {text.length} / {section.maxChars} characters
            </span>
          )}
        </div>
      )}
      {section.format === 'choice' ? (
        <span className={styles.choice}>{text}</span>
      ) : section.format === 'list' && Array.isArray(section.value) ? (
        <ListValue items={section.value.map((item) => closedWording(item, closed))} />
      ) : section.format === 'markdown' ? (
        <Markdown text={text} />
      ) : (
        <p className={styles.text}>{text}</p>
      )}
    </div>
  );
}

// `closed`: the task was skipped, rejected or dropped, so nothing is left to approve.
export function DraftSections({
  sections,
  closed = false,
}: {
  sections: DraftSection[];
  closed?: boolean;
}) {
  const context = sections.filter((section) => section.role === 'context');
  const content = sections.filter((section) => section.role === 'content');
  const checks = sections.filter((section) => section.role === 'check');
  const why = sections.filter((section) => section.role === 'why');

  return (
    <div className={styles.draft}>
      {context.length > 0 && (
        <dl className={styles.context}>
          {context.map((section) => (
            <div className={styles.contextItem} key={section.key}>
              <dt className={styles.label}>{section.label}</dt>
              <dd className={styles.contextValue}>{asText(section.value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {content.length > 0 && (
        <div className={styles.content}>
          {content.map((section) => (
            <ContentField
              key={section.key}
              section={section}
              showLabel={content.length > 1}
              closed={closed}
            />
          ))}
        </div>
      )}

      {checks.map((section) => (
        <Callout
          key={section.key}
          variant="warning"
          title={closed ? CLOSED_CHECKS_TITLE : section.label}
        >
          {Array.isArray(section.value) ? (
            <ListValue items={section.value} ordered />
          ) : (
            <Markdown text={section.value} />
          )}
        </Callout>
      ))}

      {why.map((section) => (
        <div className={styles.why} key={section.key}>
          <span className={styles.label}>{section.label}</span>
          <Markdown text={asText(section.value)} />
        </div>
      ))}
    </div>
  );
}
